import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { EmptyState } from "../components/EmptyState";
import { Title } from "../components/Title";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { useGetStoreInvoicesQuery, useGenerateStoreInvoiceMutation } from "../query/invoicesQuery";
import { StoreInvoiceResultPanel } from "./components/StoreInvoiceResultPanel";
import { formatDate, formatPeriod } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatMoney";
import { spacing } from "@styling/sizing";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import type { RestInvoiceData } from "@KassaroAB/shared/types/invoices/storeInvoice";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x4}px;
`;

const formatTotals = (totalAmount: Money): string => {
  if (!totalAmount) return "–";
  return formatMoney(totalAmount);
};

export const StoreInvoicingView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const { data: invoices, isLoading } = useGetStoreInvoicesQuery(storeId as StoreId | undefined);
  const { mutate: generateInvoice, isPending: isGenerating } =
    useGenerateStoreInvoiceMutation(storeId as StoreId | undefined);
  const [selectedInvoice, setSelectedInvoice] = useState<RestInvoiceData | null>(null);
  const [generatedInvoice, setGeneratedInvoice] = useState<RestInvoiceData | null>(null);

  const handleGenerate = () => {
    generateInvoice(undefined, {
      onSuccess: (data) => setGeneratedInvoice(data),
    });
  };

  if (isLoading) {
    return (
      <Container>
        <PageHeader>
          <Title>{t("invoice.sectionTitle")}</Title>
        </PageHeader>
        <LoadingContainer message={t("invoice.loading")} />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Title>{t("invoice.sectionTitle")}</Title>
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? t("invoice.generating") : t("invoice.generateButton")}
        </Button>
      </PageHeader>

      {!invoices || invoices.length === 0 ? (
        <Card>
          <EmptyState message={t("invoice.emptyList")} />
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("invoice.period")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.generatedAt")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.total")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.sent")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {[...invoices]
                .sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime())
                .map((invoice) => (
                  <TableRow
                    key={invoice.period}
                    clickable
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <TableCell noWrap>{formatPeriod(invoice.period)}</TableCell>
                    <TableCell noWrap>{formatDate(invoice.generatedAt)}</TableCell>
                    <TableCell noWrap>{formatTotals(invoice.totalAmount)}</TableCell>
                    <TableCell noWrap>{invoice.sent ?? false ? t("common.yes") : t("common.no")}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {selectedInvoice && (
        <StoreInvoiceResultPanel
          invoice={selectedInvoice}
          storeId={storeId as StoreId | undefined}
          onClose={() => setSelectedInvoice(null)}
          onInvoiceUpdated={setSelectedInvoice}
        />
      )}
      {generatedInvoice && (
        <StoreInvoiceResultPanel
          invoice={generatedInvoice}
          storeId={storeId as StoreId | undefined}
          onClose={() => setGeneratedInvoice(null)}
          onInvoiceUpdated={setGeneratedInvoice}
        />
      )}
    </Container>
  );
};
